import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { AuthContext, isMobileDevice } from '@repo-level/core-front';
import { Link, useCurrentRoute } from 'react-navi';
import { useTranslation } from 'react-i18next';
import { useHeaderMenuList } from 'hooks/headerMenuList';
import { resetCachePayments } from 'helpers/payments';
import { useTheme } from 'hooks/theme';
import { useAnalytics } from 'helpers/analytics';
import { AnalyticsEventName } from 'api/analytics';
import { ScMenuDesktop, ScNavIndicator } from './styled';
import { useMedia } from 'modules/common/hooks/useMedia';

interface MenuDesktopProps {
  onClick?: () => void;
}

export const MenuDesktop: FC<MenuDesktopProps> = ({ onClick }) => {
  const { t } = useTranslation();
  const { menu } = useTheme();
  const { sendEventToAnalytics } = useAnalytics();

  const {
    url: { href: currentHref },
  } = useCurrentRoute();

  const { isAuthenticated, resetUser } = useContext(AuthContext);
  const { isMobile } = useMedia();

  const [activeItemOffsetTop, setActiveItemOffsetTop] = useState(0);

  const handleClose = async () => {
    await resetUser();
    await resetCachePayments();
    onClick && onClick();
  };

  const menuList = useHeaderMenuList();

  const checkItemActive = useCallback(
    (url) => {
      return (
        currentHref === url ||
        currentHref.includes(`${url}/`) ||
        currentHref.includes(`${url}?`) ||
        currentHref.includes(`${url}#`)
      );
    },
    [currentHref]
  );

  const noActiveMenuItem = useMemo(
    () => !menuList.some(({ url }) => checkItemActive(url)),
    [menuList, checkItemActive]
  );

  const handleHelpdeskClick = async () => {
    await sendEventToAnalytics({
      name: AnalyticsEventName.NAVBAR__MENU_ITEM__BUTTON_CS__CLICK,
    });
  };

  const handleLogoutClick = async () => {
    await sendEventToAnalytics({
      name: AnalyticsEventName.NAVBAR__MENU_ITEM__BUTTON_LOGOUT__CLICK,
    });
    handleClose();
  };

  return (
    <ScMenuDesktop isMobile={isMobileDevice}>
      {!isMobile && activeItemOffsetTop && !noActiveMenuItem ? (
        <ScNavIndicator top={activeItemOffsetTop} />
      ) : null}

      {menuList.map(
        ({ name, url, icon, iconSettings, badge, isHidden }, index) => {
          const activeItem = checkItemActive(url);

          return (
            !isHidden && (
              <li
                key={index}
                className={activeItem ? 'active' : ''}
                ref={(node) => {
                  if (activeItem && node) {
                    setActiveItemOffsetTop(
                      node.offsetTop + node.getBoundingClientRect().height / 2
                    );
                  }
                }}
              >
                <Link
                  href={url}
                  onClick={() => {
                    onClick && onClick();
                    sendEventToAnalytics({
                      name: AnalyticsEventName.NAVBAR__MENU_ITEM__LINK__CLICK,
                      attributes: { additionalData: JSON.stringify({ url }) },
                    });
                  }}
                >
                  <div
                    className={
                      iconSettings?.isStroke ? 'stroke-icon' : 'fill-icon'
                    }
                  >
                    {icon}
                  </div>
                  <div className="name">{name}</div>
                  {badge}
                </Link>
              </li>
            )
          );
        }
      )}
      <li>
        <button
          id="intercomcustomLauncher"
          type="button"
          onClick={handleHelpdeskClick}
        >
          <div className="fill-icon">
            <menu.helpdeskIcon.component />
          </div>

          <div className="name">{t('glossary:helpdesk')}</div>
        </button>
      </li>
      {isAuthenticated && (
        <li>
          <button type="button" onClick={handleLogoutClick}>
            <div className="fill-icon">
              <menu.signOutIcon.component />
            </div>

            <div className="name">{t('glossary:signOut')}</div>
          </button>
        </li>
      )}
    </ScMenuDesktop>
  );
};
